import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PeopleOutlined,
  ApartmentOutlined,
  CloudDownloadOutlined,
  UploadFileOutlined,
  CategoryOutlined,
  DescriptionOutlined,
  ShowChartOutlined,
  ComputerOutlined,
  ScheduleOutlined,
  SwapHorizOutlined,
  PhotoCameraOutlined,
  MarkEmailReadOutlined,
} from '@mui/icons-material';
import { useUser } from '../../hooks/useUser';
import { isSuperAdmin, isAdminUser } from '../../sp-types/userType';

interface AdminCard {
  title: string;
  description: string;
  link: string;
  icon: React.ReactNode;
  superadminOnly?: boolean;
}

const ICON_SIZE = 28;

const ADMIN_CARDS: AdminCard[] = [
  {
    title: 'Users',
    description: 'Manage users, roles & permissions',
    link: '/admin/users',
    icon: <PeopleOutlined sx={{ fontSize: ICON_SIZE }} />,
  },
  {
    title: 'Accounts',
    description: 'Manage client accounts',
    link: '/admin/accounts',
    icon: <ApartmentOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Materials',
    description: 'Materials & formula assignments',
    link: '/admin/materials',
    icon: <CategoryOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Contracts',
    description: 'Contract management & terms',
    link: '/admin/contracts',
    icon: <DescriptionOutlined sx={{ fontSize: ICON_SIZE }} />,
  },
  {
    title: 'Data Fetch',
    description: 'Refinitiv price fetch runs & status',
    link: '/admin/data-fetch',
    icon: <CloudDownloadOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Import Batches',
    description: 'File uploads & batch processing',
    link: '/admin/imports',
    icon: <UploadFileOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Volatility Risk',
    description: 'Formula volatility calculations',
    link: '/admin/volatility',
    icon: <ShowChartOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Cron Jobs',
    description: 'Scheduled tasks & run history',
    link: '/admin/cron-jobs',
    icon: <ScheduleOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'VM Management',
    description: 'Fetcher VM status & controls',
    link: '/admin/vm-management',
    icon: <ComputerOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Material Mappings',
    description: 'SAP code → formula mappings per worker',
    link: '/admin/material-mappings',
    icon: <SwapHorizOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Account Snapshots',
    description: 'Daily point-in-time snapshots of account state',
    link: '/admin/snapshots',
    icon: <PhotoCameraOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
  {
    title: 'Email Ingestions',
    description: 'Emailed data files: aliases, inbox & import status',
    link: '/admin/email-ingestions',
    icon: <MarkEmailReadOutlined sx={{ fontSize: ICON_SIZE }} />,
    superadminOnly: true,
  },
];

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: user } = useUser();
  const isSuperadmin = isSuperAdmin(user);
  const isAdmin = isAdminUser(user);

  const visibleCards = ADMIN_CARDS.filter((card) => {
    if (card.superadminOnly) return isSuperadmin;
    return isAdmin;
  });

  return (
    <div className="px-4 py-3">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-left" style={{ color: '#111827' }}>Admin</h1>
        <p className="text-xs mt-0.5 text-left" style={{ color: '#6b7280' }}>
          System management & configuration
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {visibleCards.map((card) => (
          <button
            key={card.link}
            onClick={() => navigate(card.link)}
            className="bg-white rounded-lg p-4 text-left transition-all"
            style={{
              border: '1px solid #E6E9EE',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#3B82F6';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(59,130,246,0.10)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E6E9EE';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center rounded-lg flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  background: '#F1F4F8',
                  color: '#3B82F6',
                }}
              >
                {card.icon}
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: '#111827' }}>{card.title}</h3>
                <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>{card.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
